<script lang="ts">
	import { onMount } from 'svelte'
	import { writable } from 'svelte/store'
	import { queryParam } from 'sveltekit-search-params'

	import { afterNavigate, beforeNavigate } from '$app/navigation'
	import * as Card from '$lib/components/ui/card'
	import { LINE_CHART_RANGES, LINE_CHART_RANGES_LIST } from '$lib/constants/charts'
	import type { ChainId, ChartRange, LineChartRecord } from '$lib/models'
	import * as m from '$lib/paraglide/messages'

	import RadioSwitch from './radio-switch.svelte'
	import TpsChart from './tps-chart.svelte'

	export let title: string
	export let titleTag: Card.HeadingLevel
	export let chainIds: ChainId[]
	export let chartData: {
		records: LineChartRecord[]
		range: ChartRange
	}

	const chartRangeQuery = queryParam<ChartRange>('range-tc')

	const chartRange = writable<ChartRange>(
		$chartRangeQuery && LINE_CHART_RANGES_LIST.includes($chartRangeQuery) ? $chartRangeQuery : 'day'
	)

	let isLoading = true
	let loaderTimeout: ReturnType<typeof setTimeout>

	beforeNavigate(({ from, to }) => {
		if (!from || !to) return

		const navigatingWithinPage = from.url.pathname === to.url.pathname
		const queryParamChanged =
			from.url.searchParams.get('range-tc') !== to.url.searchParams.get('range-tc')

		if (navigatingWithinPage && queryParamChanged) {
			clearTimeout(loaderTimeout)

			loaderTimeout = setTimeout(() => {
				isLoading = true
			}, 100)
		}
	})

	afterNavigate(() => {
		clearTimeout(loaderTimeout)

		isLoading = false
	})

	onMount(() => {
		const unsubscibeFromChartRangeQuery = chartRangeQuery.subscribe(value => {
			if (value && LINE_CHART_RANGES_LIST.includes(value)) {
				chartRange.set(value)
			} else {
				chartRange.set('day')
			}
		})

		const unsubscribeFromChartRange = chartRange.subscribe(value => {
			if (value !== $chartRangeQuery) {
				if (value === 'day') {
					chartRangeQuery.set(null)
				} else {
					chartRangeQuery.set(value)
				}
			}
		})

		return () => {
			unsubscribeFromChartRange()
			unsubscibeFromChartRangeQuery()
		}
	})
</script>

<Card.Root>
	<Card.Header class="flex-row! items-center! justify-between gap-2">
		<Card.Title tag={titleTag}>
			{title}
		</Card.Title>

		<RadioSwitch
			name="chart-range"
			ariaLabel={m.chart_switch_label()}
			disabled={isLoading}
			options={LINE_CHART_RANGES}
			bind:value={$chartRange}
		/>
	</Card.Header>

	<Card.Content>
		<TpsChart {chainIds} chartData={chartData.records} chartRange={$chartRange} {isLoading} />
	</Card.Content>
</Card.Root>
